import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { ArrowLeft, Code, Palette, Layout, Zap } from 'lucide-react';

export default function DevGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4">Developer Guide</h1>
          <p className="text-xl text-muted-foreground">
            EduLive - JHS Interactive Classroom Platform
          </p>
        </div>

        {/* Design System */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Palette className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Design System</h2>
          </div>
          
          <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
            <div>
              <h3 className="font-semibold mb-3">Colors</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="w-full h-20 rounded-xl bg-primary mb-2" />
                  <p className="font-medium">Primary (Indigo)</p>
                  <p className="text-sm text-muted-foreground">#6366F1</p>
                </div>
                <div>
                  <div className="w-full h-20 rounded-xl bg-accent mb-2" />
                  <p className="font-medium">Accent (Emerald)</p>
                  <p className="text-sm text-muted-foreground">#10B981</p>
                </div>
                <div>
                  <div className="w-full h-20 rounded-xl bg-orange mb-2" />
                  <p className="font-medium">Orange</p>
                  <p className="text-sm text-muted-foreground">#F59E0B</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Typography</h3>
              <p className="text-muted-foreground">Font Family: Inter</p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Border Radius</h3>
              <p className="text-muted-foreground">Standard: 14px (0.875rem)</p>
            </div>
          </div>
        </section>

        {/* Page Structure */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Layout className="w-6 h-6 text-accent" />
            </div>
            <h2 className="text-2xl font-bold">Pages & Routes</h2>
          </div>
          
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4">Route</th>
                  <th className="text-left p-4">Component</th>
                  <th className="text-left p-4">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-mono text-sm">/</td>
                  <td className="p-4">LandingPage</td>
                  <td className="p-4 text-muted-foreground">Hero, features, CTA</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/teacher/dashboard</td>
                  <td className="p-4">TeacherDashboard</td>
                  <td className="p-4 text-muted-foreground">Main teacher interface</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/teacher/lesson/:id</td>
                  <td className="p-4">LessonBuilder</td>
                  <td className="p-4 text-muted-foreground">Slide editor with 18+ types</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/teacher/live/:id</td>
                  <td className="p-4">LiveSessionTeacher</td>
                  <td className="p-4 text-muted-foreground">Live session control</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/join</td>
                  <td className="p-4">StudentJoin</td>
                  <td className="p-4 text-muted-foreground">Session code entry</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/student/live/:id</td>
                  <td className="p-4">StudentLiveView</td>
                  <td className="p-4 text-muted-foreground">Student activity view</td>
                </tr>
                <tr>
                  <td className="p-4 font-mono text-sm">/demos</td>
                  <td className="p-4">DemoActivities</td>
                  <td className="p-4 text-muted-foreground">Activity showcase</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Activity Types */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-orange/10 flex items-center justify-center">
              <Zap className="w-6 h-6 text-orange" />
            </div>
            <h2 className="text-2xl font-bold">Interactive Activities</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              'Multiple Choice Quiz',
              'Open-Ended Question',
              'Poll',
              'Fill in the Blanks',
              'Matching Pairs',
              'Time to Climb',
              'Draw-It',
              'Collaborate Board',
              'Memory Test',
              'Matching Images',
              'Ordering Activity',
              'Label the Diagram',
              'True or False',
              'Short Answer',
              'Math Equation',
              'Word Cloud',
              'Quiz with Images',
              'Interactive Timeline',
            ].map((activity, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 bg-card rounded-xl border border-border"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  {index + 1}
                </div>
                <span className="font-medium">{activity}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Tech Stack */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-chart-4/10 flex items-center justify-center">
              <Code className="w-6 h-6 text-chart-4" />
            </div>
            <h2 className="text-2xl font-bold">Tech Stack</h2>
          </div>
          
          <div className="bg-card rounded-2xl border border-border p-6">
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>React 18</strong> - UI library</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>React Router 7</strong> - Routing (Data mode)</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Tailwind CSS v4</strong> - Styling</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Motion (Framer Motion)</strong> - Animations</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>react-dnd</strong> - Drag & Drop</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Recharts</strong> - Data visualization</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Lucide React</strong> - Icons</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Sonner</strong> - Toast notifications</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span><strong>Radix UI</strong> - Accessible components</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Backend Integration */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Laravel Backend Integration</h2>
          
          <div className="bg-card rounded-2xl border border-border p-6">
            <p className="mb-4">This frontend is ready to integrate with a Laravel backend. Key integration points:</p>
            <ul className="space-y-2 ml-4">
              <li className="text-muted-foreground">• User authentication (teachers vs students)</li>
              <li className="text-muted-foreground">• Lesson CRUD operations</li>
              <li className="text-muted-foreground">• Live session management with WebSockets</li>
              <li className="text-muted-foreground">• Real-time student responses</li>
              <li className="text-muted-foreground">• Analytics and reporting data</li>
              <li className="text-muted-foreground">• File uploads (images, PDFs, etc.)</li>
            </ul>
          </div>
        </section>

        <div className="text-center">
          <Button size="lg" onClick={() => navigate('/demos')} className="rounded-xl">
            Explore Interactive Demos
          </Button>
        </div>
      </div>
    </div>
  );
}
